import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const ChapterCards = () => {
  const [classificationsData, setClassificationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // Hook for navigation

  useEffect(() => {
    const fetchClassificationsData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/disease-classifications');
        const data = await response.json();

        if (data.success) {
          const filteredData = data.data.filter(item =>
            item.disease_count >= 5 && 
            item.gene_count >= 5 &&
            !isNaN(parseInt(item.chapter.replace(/^0+/, '')))
          );

          filteredData.sort((a, b) => b.gene_count - a.gene_count);
          setClassificationsData(filteredData);
        } else {
          setError(data.message || 'Failed to load disease classification data');
        }
      } catch (err) {
        setError(`Error: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClassificationsData();
  }, []);

  // Handle card click to navigate
  const handleCardClick = (chapter) => {
    navigate('/disease-classifications', { state: { selectedChapter: chapter } });
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading chapters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9v4a1 1 0 11-2 0v-4a1 1 0 112 0zm0-4a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!classificationsData.length) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="text-center py-4 text-gray-500">
          No chapter data available
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {classificationsData.slice(0, 4).map((chapter) => (
        <div
          key={chapter.chapter}
          className="bg-white p-6 shadow rounded-lg border-l-4 border-blue-500 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => handleCardClick(chapter.chapter)}
        >
          <h3 className="text-xl font-medium text-gray-800">
            ICD-11 Chapter {chapter.chapter}: {chapter.name}
          </h3>
          <p className="mt-2 text-gray-600">
            {chapter.description}
          </p>
          <div className="mt-4 flex justify-between items-center">
            <div className="flex space-x-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700">
                <span className="font-semibold mr-1">{chapter.gene_count}</span> genes
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700">
                <span className="font-semibold mr-1">{chapter.disease_count}</span> diseases
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

ChapterCards.propTypes = {
  // Removed onChapterSelect since we're using navigation instead
};

export default ChapterCards;